import AnalyticsEventsCollectorInterface from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsCollectorInterface";
import { Observable, filter, take } from "rxjs";
import { container } from "tsyringe";

import SdkAnalyticsEventsFactory from "./analytics/SdkAnalyticsEventsFactory";
import { ERROR_CODES, PROMISE_TIMEOUT_MS } from "./constants";
import { SdkEventInterface } from "./events/SdkEventInterface";
import SdkError from "./methods/SdkError";
import { SdkMethodPayloadInterface } from "./methods/SdkMethodPayloadInterface";
import { SdkModule } from "./SdkModule";
import { ErrorResult } from "./types";

interface CustomSubscription {
    unsubscribe: () => void;
}

export function subscribeToSdkMethod<
    T extends SdkMethodPayloadInterface = never
>(
    methodName: T["methodName"],
    callback: (payload: T) => void
): CustomSubscription {
    const sdkMethodsCollector =
        container.resolve(SdkModule).exports.sdkMethodsCollector;
    sdkMethodsCollector.addSubscriber<T>(methodName, callback);

    return {
        unsubscribe() {
            sdkMethodsCollector.deleteSubscriber(methodName, callback);
        },
    };
}

export function checkSubscribers(
    methodName: string,
    reject: (error: ErrorResult) => void
) {
    const sdkMethodsCollector =
        container.resolve(SdkModule).exports.sdkMethodsCollector;
    if (sdkMethodsCollector.hasSubscribers(methodName)) return;

    reject(
        new SdkError(
            ERROR_CODES.NO_SUBSCRIBERS,
            "Method execution failed: no subscribers found"
        )
    );
}

export function dispatchSdkEvent(event: SdkEventInterface) {
    container.resolve(SdkModule).exports.sdk.dispatch(event);
}

export function promiseWithTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number = PROMISE_TIMEOUT_MS
) {
    let timeout: ReturnType<typeof setTimeout>;

    const timeoutPromise = new Promise<ErrorResult>((resolve, reject) => {
        timeout = setTimeout(() => {
            reject(
                new SdkError(
                    ERROR_CODES.TIMEOUT_ERROR,
                    `The operation did not complete within ${
                        timeoutMs / 1000
                    } seconds. Please try again.`
                )
            );
        }, timeoutMs);
    });

    return Promise.race([promise, timeoutPromise])
        .then((result) => {
            clearTimeout(timeout);
            return result;
        })
        .catch((error) => {
            clearTimeout(timeout);
            throw error;
        });
}

export function checkRequiredArguments(
    methodName: string,
    requiredCount: number,
    presentCount: number,
    reject: (error: ErrorResult) => void
) {
    if (presentCount < requiredCount) {
        reject(
            new SdkError(
                ERROR_CODES.INSUFFICIENT_ARGS,
                `Failed to execute '${methodName}' on 'sdk': ${requiredCount} arguments required, but only ${presentCount} present.`
            )
        );
    }
}

/**
 * This is temporary function to send an event when the `runIfModuleReady` method is called from `sdk.common.events`.
 * It is used to track the usage of this method and will be removed in the future.
 */
export function sendRunIfModuleReadyCalledEvent(
    eventsCollector: AnalyticsEventsCollectorInterface,
    sdkEventsFactory: SdkAnalyticsEventsFactory,
    isEventsSenderStarted$: Observable<boolean>
) {
    try {
        const today = new Date().toISOString().split("T")[0];
        const storageKey = "runIfModuleReady_called_date";

        if (localStorage.getItem(storageKey) !== today) {
            localStorage.setItem(storageKey, today);

            const eventData = sdkEventsFactory.createMethodCalled(
                "runIfModuleReady",
                "sdk.common.events"
            );

            isEventsSenderStarted$
                .pipe(
                    filter((isStarted) => isStarted),
                    take(1)
                )
                .subscribe(() => {
                    eventsCollector.pushEvent(eventData);
                });
        }
    } catch {
        /* empty */
    }
}
